/**
 * This context is for globally managing user authentication and details update and retrival
 */

import { useEffect, useState, createContext } from 'react';

/**
 * Context for managing user authentication and details.
 *
 * @typedef {Object} UserContextType
 * @property {boolean} isLoggedIn - Indicates if a user is logged in.
 * @property {Function} setIsLoggedIn - Function to update login status.
 * @property {Object} userDetails - User information retrieved from the backend.
 * @property {Function} setUserDetails - Function to update user details.
 * @property {boolean} isLoading - Indicates if the user data is being fetched.
 * @property {Function} fetchUserDetails - Fetches user details from the backend.
 */

/** Creates a UserContext to manage authentication state globally. */
export const UserContext = createContext();

/**
 * UserProvider Component
 *
 * This component provides user authentication details and session state
 * across the entire application using React Context.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - Child components wrapped by the provider.
 * @returns {JSX.Element} The rendered UserProvider component.
 */
export const UserProvider = ({ children }) => {
  // State to track login status
  const [isLoggedIn, setIsLoggedIn] = useState(null);

  // State to track loading status while fetching user data
  const [isLoading, setIsLoading] = useState(true);

  // State to store user details
  const [userDetails, setUserDetails] = useState({});

  // State to store roadmaps fetched from the backend
  const [roadmaps, setRoadmaps] = useState([]);

  // State to track if roadmaps are being fetched
  const [roadmapFetching, setRoadmapFetching] = useState(false);

  /**
   * Fetches user details from the backend.
   * If a valid user ID exists in local storage, it makes a request to get user data.
   * Otherwise, it clears the stored user ID.
   */
  const fetchUserDetails = async () => {
    try {
      const userEmail = localStorage.getItem('u_e');
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/u/get-user?e=${userEmail}`,
        {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          // credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        setIsLoggedIn(true);
        setUserDetails(responseData);
      } else {
        setIsLoggedIn(false);
        localStorage.removeItem('u_e');
        localStorage.removeItem('session_token');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetches all roadmaps associated with the user.
   * This function is called after user details are successfully fetched.
   */
  const fetchAllRoadmapOfUser = async () => {
    try {
      setRoadmapFetching(true);
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/r/get-all-roadmap?e=${userDetails?.email}&t=public`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          // credentials: 'include',
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        setRoadmaps(sortRoadmapCards(responseData));
      }
    } catch (error) {
    } finally {
      setRoadmapFetching(false);
    }
  };

  const sortRoadmapCards = (roadmaps) => {
  const regex = /^(.*) level (\d+)$/i;

  return roadmaps.sort((a, b) => {
    const matchA = a.title.toLowerCase().match(regex);
    const matchB = b.title.toLowerCase().match(regex);

    const isLevelA = !!matchA;
    const isLevelB = !!matchB;

    // Prioritize items with level
    if (isLevelA && !isLevelB) return -1;
    if (!isLevelA && isLevelB) return 1;

    // If both are non-level items, keep original order or sort alphabetically
    if (!isLevelA && !isLevelB) {
      return a.title.localeCompare(b.title);
    }

    // If both have levels, sort by name and then level
    const [, nameA, levelA] = matchA;
    const [, nameB, levelB] = matchB;

    const nameCompare = nameA.localeCompare(nameB);
    if (nameCompare !== 0) return nameCompare;

    return parseInt(levelA) - parseInt(levelB);
  });
};


  /**
   * Runs once when the component mounts to fetch user details.
   */
  useEffect(() => {
    const userEmail = localStorage.getItem('u_e');
    if (userEmail) {
      fetchUserDetails();
    } else {
      setIsLoading(false);
      setIsLoggedIn(false);
    }
  }, []);

  return (
    <UserContext.Provider
      value={{
        isLoggedIn,
        setIsLoggedIn,
        userDetails,
        setUserDetails,
        isLoading,
        roadmaps,
        setRoadmaps,
        roadmapFetching,
        setRoadmapFetching,
        fetchUserDetails,
        fetchAllRoadmapOfUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
